import type { Entity2D, KeyByComponent } from '..';
import type { Entity, EntityClass } from '.';
import { PoolGroup } from '.';
import type { ComponentClass } from './Component';
import { UIDs } from './Entity';

const ctors: Map<number, Set<ComponentClass>> = new Map();

/**
 * Creates a class that extends some other entity class. Any components passed will be added on `new Class()`
 * You can think of this as defining a "prefab".
 * @param Constructor - Class to inherit from
 * @param components - components to add to the entity on creation
 * @returns - A class that extends from the class provided
 */
export function DefineEntity<E extends Entity, C extends ComponentClass[]>(
  Constructor: EntityClass<[], [], {}, E>,
  ...components: C
) {
  const curr = ctors.get(Constructor.ID) ?? new Set<ComponentClass>();
  const entityClass = class extends Constructor {};
  const value = new Set<ComponentClass>([...curr, ...components]);

  entityClass.ID = UIDs._ENTITY_CLASS++;
  ctors.set(entityClass.ID, value);

  Object.defineProperty(entityClass.prototype, '_staticComponents', {
    value,
    writable: true,
  });

  // type system abuse
  return entityClass as unknown as EntityClass<
    C,
    [],
    E['c'],
    Entity<C, [], E['c']> & E
  >;
}

/**
 * Creates an entity and initialises it
 * @param Class - Class to be created
 * @param data - data passed to the init function
 * @returns Instance of the class provided
 */
export function createEntity<E extends Entity>(
  Class: EntityClass<[], [], {}, E>,
  data?: KeyByComponent<E['c']>,
): E {
  if (!Class.POOL) {
    const entity = new Class();

    entity.init(data);

    return entity;
  }

  return PoolGroup.get(Class, data);
}

/**
 * removes an entity from the scene and allows for the entity to be returned to the pool
 * do not use the entity after calling this function
 * @param entity - entity to be released
 */
export function releaseEntity(entity: Entity, releaseChildren = true): void {
  const scene = entity.scene;

  const release = (ent: Entity2D) => {
    if (releaseChildren && ent.c.container) {
      ent.c.container.traverse((child: Entity2D) => {
        if (child === ent) return;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!child.scene) quickRemove(child);
        else {
          child._released = true;
          scene?.removeFromScene(child);
        }
      });
    }
  };

  const quickRemove = (ent: Entity) => {
    release(ent as Entity2D);
    ent._pooled ? PoolGroup.return(ent) : ent.reset?.();
    ent._gc = false;
    ent._released = false;
  };

  if (!scene) quickRemove(entity);
  else {
    entity._released = true;
    scene.removeFromScene(entity);
    release(entity as Entity2D);
  }
}
